'use client'

import { Box, HStack, Text } from '@chakra-ui/react'
import { Page } from '@saas-ui/react'

import { SidebarTrigger } from '#components/sidebar-trigger.tsx'

/**
 * Chat column / header title-zone width. Shared by the session page and its
 * skeleton so the header divider always lands on the column split below it.
 */
export const CHAT_WIDTH = '480px'

interface SessionHeaderProps {
  /**
   * Session title. A string is rendered as the truncating title text; any other
   * node (e.g. a `Skeleton`) is rendered as-is so placeholder headers keep the
   * exact same layout.
   */
  title: React.ReactNode
  actions?: React.ReactNode
  /** Overflow menu rendered right after the session title. */
  menu?: React.ReactNode
}

/**
 * Session page header. Overrides the default Saas UI page-header grid so the
 * title zone is exactly `CHAT_WIDTH` wide, starting flush with the page's left
 * edge (the sidebar trigger lives inside it). This makes the divider between
 * the title zone and the toolbar land at the same x-offset as the split between
 * the chat column and the right pane below.
 */
export function SessionHeader({ title, actions, menu }: SessionHeaderProps) {
  return (
    <Page.Header
      css={{
        px: 0,
        columnGap: 0,
        gridTemplateAreas: '"heading actions"',
        gridTemplateColumns: `${CHAT_WIDTH} 1fr`,
      }}
      title={
        <HStack
          gridArea="heading"
          gap="2"
          px="3"
          minW="0"
          w="full"
          h="full"
          borderRightWidth="1px"
          borderColor="border"
        >
          <SidebarTrigger />
          {typeof title === 'string' ? (
            <Text textStyle="sm" fontWeight="medium" truncate title={title}>
              {title}
            </Text>
          ) : (
            title
          )}
          {menu}
        </HStack>
      }
      actions={
        actions ? (
          <Box gridArea="actions" px="3" minW="0">
            {actions}
          </Box>
        ) : undefined
      }
    />
  )
}
